import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Table, Button, Input, Tag, Avatar, Flex, Typography, Space, Select, Tooltip, Popconfirm, message, Spin } from "antd";
import { UserAddOutlined, EditOutlined, EyeOutlined, SearchOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getCookie } from "../../utils/cookieUtils";
import AddEmployeeModal from "./AddEmployeeModal";
import EmployeeDashboardView from "./EmployeeDashboardView";
import { getEmployees, createEmployee, updateEmployee, updateEmployeeTags, deleteEmployee } from "../../services/employeeService";
import { getCompanySettings } from "../../services/organizationService";

const { Text } = Typography;

function EmployeesWorkspace() {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  const [employees, setEmployees] = useState([]);
  const [companyTags, setCompanyTags] = useState([]);
  const [collaboratingCompanies, setCollaboratingCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTagFilter, setSelectedTagFilter] = useState("all");

  // Modals & Drawers
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  // Active Employee Full-Page View
  const [activeEmployee, setActiveEmployee] = useState(null);

  // Inline Tag Editing
  const [editingTagEmpId, setEditingTagEmpId] = useState(null);
  const [inlineTagInput, setInlineTagInput] = useState("");

  // Fetch Employees from Live Middleware
  const loadEmployeesData = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setLoading(true);
    try {
      const [res, settingsRes] = await Promise.all([
        getEmployees(effectiveCompanyCode),
        getCompanySettings(effectiveCompanyCode),
      ]);
      if (res?.success && Array.isArray(res.employees)) {
        setEmployees(res.employees);
      } else {
        setEmployees([]);
      }
      if (settingsRes?.success && Array.isArray(settingsRes.settings?.tags)) {
        setCompanyTags(settingsRes.settings.tags);
      }
      if (settingsRes?.success && Array.isArray(settingsRes.settings?.collaboratingCompanies)) {
        setCollaboratingCompanies(settingsRes.settings.collaboratingCompanies);
      }
    } catch (err) {
      console.error("Failed to load employees from middleware:", err);
      message.error("Failed to load employees.");
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyCode]);

  useEffect(() => {
    loadEmployeesData();
  }, [loadEmployeesData]);

  // Available Tags from DB settings, Redux & employees
  const availableTags = useMemo(() => {
    const tagSet = new Set(Array.isArray(companyTags) ? companyTags : []);
    employees.forEach((emp) => {
      if (Array.isArray(emp.tags)) {
        emp.tags.forEach((t) => {
          if (t && String(t).trim()) tagSet.add(String(t).trim());
        });
      }
    });
    return Array.from(tagSet);
  }, [companyTags, employees]);

  // Filtered Employees
  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const empTags = Array.isArray(emp.tags) ? emp.tags : [];
      if (selectedTagFilter !== "all" && !empTags.includes(selectedTagFilter)) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (emp.name || "").toLowerCase().includes(q) || (emp.mobile || "").includes(q) || (emp.employeeCode || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [employees, selectedTagFilter, searchQuery]);



  // Add / Edit Employee
  const handleOpenAddModal = (emp = null) => {
    setEditingEmployee(emp);
    setIsAddModalOpen(true);
  };

  const handleSaveEmployee = async (empData) => {
    try {
      if (editingEmployee && (editingEmployee._id || editingEmployee.id)) {
        const empId = editingEmployee._id || editingEmployee.id;
        const res = await updateEmployee(empId, empData, effectiveCompanyCode);
        if (res.success) {
          message.success("Employee details updated successfully!");
          loadEmployeesData();
        } else {
          message.error(res.message || "Failed to update employee.");
        }
      } else {
        const res = await createEmployee(empData, effectiveCompanyCode);
        if (res.success) {
          message.success("Employee added successfully!");
          loadEmployeesData();
        } else {
          message.error(res.message || "Failed to create employee.");
        }
      }
      setIsAddModalOpen(false);
      setEditingEmployee(null);
    } catch (err) {
      console.error("Failed to save employee:", err);
      message.error("Failed to save employee.");
    }
  };

  const handleDeleteEmployee = async (empId) => {
    try {
      const res = await deleteEmployee(empId, effectiveCompanyCode);
      if (res?.success) {
        message.success("Employee removed successfully!");
        loadEmployeesData();
      } else {
        message.error(res?.message || "Failed to remove employee.");
      }
    } catch (err) {
      console.error("Failed to remove employee:", err);
      message.error("Failed to remove employee.");
    }
  };

  // Save Inline Tag
  const handleSaveInlineTag = async (empId, currentTags = []) => {
    if (!inlineTagInput.trim()) {
      setEditingTagEmpId(null);
      return;
    }
    const updatedTags = Array.from(new Set([...currentTags, inlineTagInput.trim()]));
    try {
      const res = await updateEmployeeTags(empId, updatedTags, effectiveCompanyCode);
      if (res.success) {
        message.success("Employee tags updated!");
        loadEmployeesData();
      }
    } catch (e) {
      console.error("Failed to update tags:", e);
      message.error("Failed to update tags.");
    }
    setEditingTagEmpId(null);
    setInlineTagInput("");
  };

  const handleRemoveTag = async (empId, currentTags, tagToRemove) => {
    const safeTags = Array.isArray(currentTags) ? currentTags : [];
    const updatedTags = safeTags.filter((t) => t !== tagToRemove);
    try {
      const res = await updateEmployeeTags(empId, updatedTags, effectiveCompanyCode);
      if (res.success) {
        message.success("Tag removed!");
        loadEmployeesData();
      }
    } catch (e) {
      console.error("Failed to remove tag:", e);
      message.error("Failed to remove tag.");
    }
  };

  // If viewing an active employee detail dashboard
  if (activeEmployee) {
    return <EmployeeDashboardView employee={activeEmployee} companyCode={effectiveCompanyCode} onBack={() => setActiveEmployee(null)} />;
  }

  const columns = [
    {
      title: "Employee",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Flex align="center" gap={12}>
          <Avatar
            src={record.profilePhoto || undefined}
            style={{
              backgroundColor: "#2459d3",
              fontWeight: 600,
            }}
          >
            {(name || "U").charAt(0).toUpperCase()}
          </Avatar>
          <Flex vertical>
            <Text strong style={{ fontSize: "0.9rem", color: "#0f172a" }}>
              {name || "Sales Rep"}
            </Text>
            <Text type="secondary" style={{ fontSize: "0.75rem" }}>
              {record.mobile || "—"} {record.countryCode ? `(${record.countryCode})` : ""}
            </Text>
          </Flex>
        </Flex>
      ),
    },
    {
      title: "Employee Code",
      dataIndex: "employeeCode",
      key: "employeeCode",
      render: (code) => <Tag style={{ borderRadius: 6, fontWeight: 600, background: "#f1f5f9" }}>#{code || "EMP"}</Tag>,
    },
    {
      title: "Tags / Territory",
      dataIndex: "tags",
      key: "tags",
      render: (tags, record) => {
        const empId = record._id || record.id;
        const tagList = Array.isArray(tags) ? tags : [];
        const isEditing = editingTagEmpId === empId;

        return (
          <Flex align="center" gap={6} wrap="wrap">
            {tagList.map((tag) => (
              <Tag
                key={tag}
                closable
                onClose={(e) => {
                  e.preventDefault();
                  handleRemoveTag(empId, tagList, tag);
                }}
                color="blue"
                style={{ borderRadius: 10, fontSize: "0.75rem" }}
              >
                {tag}
              </Tag>
            ))}
            {isEditing ? (
              <Input size="small" style={{ width: 100 }} value={inlineTagInput} onChange={(e) => setInlineTagInput(e.target.value)} onPressEnter={() => handleSaveInlineTag(empId, tagList)} onBlur={() => handleSaveInlineTag(empId, tagList)} autoFocus />
            ) : (
              <Button
                type="dashed"
                size="small"
                style={{ borderRadius: 10, fontSize: "0.7rem", height: 22 }}
                onClick={() => {
                  setEditingTagEmpId(empId);
                  setInlineTagInput("");
                }}
              >
                + Tag
              </Button>
            )}
          </Flex>
        );
      },
    },
    {
      title: "Device / App",
      key: "device",
      render: (_, r) => (
        <Flex vertical>
          <Text style={{ fontSize: "0.82rem" }}>{r.deviceModel || "Android Device"}</Text>
          <Text type="secondary" style={{ fontSize: "0.72rem" }}>
            v{r.appVersion || "1.0.0"}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Last Synced Time",
      dataIndex: "lastSyncTime",
      key: "lastSyncTime",
      render: (time) => <Text style={{ fontSize: "0.82rem", color: "#64748b" }}>{time ? new Date(time).toLocaleString("en-IN") : "Never synced"}</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, r) => {
        const empId = r._id || r.id;
        return (
          <Space size="small">
            <Tooltip title="View Rep Telephony Dashboard">
              <Button type="text" icon={<EyeOutlined style={{ color: "#2459d3" }} />} onClick={() => setActiveEmployee(r)} />
            </Tooltip>
            <Tooltip title="Edit Employee">
              <Button type="text" icon={<EditOutlined style={{ color: "#64748b" }} />} onClick={() => handleOpenAddModal(r)} />
            </Tooltip>
            <Popconfirm
              title="Delete Employee"
              description={`Are you sure you want to remove ${r.name || "this employee"}?`}
              onConfirm={() => handleDeleteEmployee(empId)}
              okText="Yes, Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete Employee">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Spin spinning={loading} description="Loading employees...">
      <Flex vertical gap={20} className="employees-workspace-container">
        {/* ── TABLE CARD ── */}
        <Card bordered={false} style={{ borderRadius: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
          {/* Header Controls */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={16} style={{ marginBottom: 20 }}>
            <Flex align="center" gap={12} wrap="wrap">
              <Input prefix={<SearchOutlined style={{ color: "#94a3b8" }} />} placeholder="Search employee name, mobile, code..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} allowClear style={{ width: 280 }} />

              <Select
                value={selectedTagFilter}
                onChange={setSelectedTagFilter}
                style={{ width: 170 }}
                options={[
                  { value: "all", label: "All Tags / Roles" },
                  ...availableTags.map((t) => ({ value: t, label: t })),
                ]}
              />
            </Flex>

            <Flex align="center" gap={12}>
              <Tag color="blue" style={{ fontSize: "0.85rem", padding: "4px 10px", borderRadius: 6, fontWeight: 600, margin: 0 }}>
                {employees.length} {employees.length === 1 ? "Member" : "Members"}
              </Tag>
              <Button type="primary" icon={<UserAddOutlined />} onClick={() => handleOpenAddModal(null)}>
                Add Employee
              </Button>
            </Flex>
          </Flex>

          {/* Ant Design Employees Table */}
          <Table columns={columns} dataSource={filteredEmployees} rowKey={(r) => r._id || r.id || r.mobile} pagination={{ pageSize: 10, showSizeChanger: true }} className="qa-antd-table" size="middle" scroll={{ x: 800 }} />
        </Card>

        {/* Add / Edit Employee Modal */}
        <AddEmployeeModal
          visible={isAddModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingEmployee(null);
          }}
          onSave={handleSaveEmployee}
          editingEmployee={editingEmployee}
          availableTags={availableTags}
          collaboratingCompanies={collaboratingCompanies}
          companyCode={effectiveCompanyCode}
        />
      </Flex>
    </Spin>
  );
}

export default EmployeesWorkspace;
